import { createStyles, makeStyles } from '@material-ui/core';
import { AbandonedBookings } from 'components/Bookings2/Abandoned/AbandonedBookings';
import { AppointmentsList } from 'components/Bookings2/List/AppointmentsList';

import { readDaycareBookingsView } from 'components/Bookings2/Daycare/Kanban/daycareViewStorage';
import { BookingsNavbar } from 'components/Bookings2/Navbar/Navbar';
import useServiceCategories from 'hooks/useServiceCategories';
import React from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router';
import { withRouter } from 'react-router-dom';
import {
    BookingsAbandonedPath,
    BookingsBoardingPath,
    BookingsDaycarePath,
    BookingsEvaluationPath,
    BookingsRequestedPath,
    BookingsAllPath,
    BookingsKanbanPath,
    BookingsPath,
    BookingsRootPath,
    BookingsGroomingPath,
    BookingsDaycareDetailPath,
    BookingsBoardingDetailPath,
    BookingsEvaluationDetailPath,
    BookingsRequestedDetailPath,
    BookingsAllDetailPath,
    BookingsGroomingDetailPath,
    BookingsTransportationPath,
    BookingsTransportationDetailPath,
    BookingsTrainingPath,
    BookingsTrainingDetailPath,
    BookingsWalkingPath,
    BookingsWalkingDetailPath
} from 'routes/Paths';
import PrivatePage from './PrivatePage';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden'
        },
        body: {
            display: 'flex',
            overflow: 'hidden',
            flexDirection: 'column',
            flex: '1 1 0',
            minHeight: 0
        }
    })
);

export const Bookings: React.FC = () => {
    const classes = useStyles();
    const location = useLocation();
    const {
        hasGrooming,
        hasMobileGrooming,
        hasDental,
        hasDaycare,
        hasBoarding,
        hasEvaluation,
        hasTransportation,
        hasTraining,
        hasWalking
    } = useServiceCategories();

    const isBookingPathAllowed = React.useCallback(
        (path: string | null): boolean => {
            if (!path) return false;

            switch (path) {
                case BookingsGroomingPath:
                    return hasGrooming || hasDental || hasMobileGrooming;
                case BookingsDaycarePath:
                case BookingsKanbanPath:
                    return hasDaycare;
                case BookingsBoardingPath:
                    return hasBoarding;
                case BookingsEvaluationPath:
                    return hasEvaluation;
                case BookingsTransportationPath:
                    return hasTransportation;
                case BookingsTrainingPath:
                    return hasTraining;
                case BookingsAbandonedPath:
                case BookingsRequestedPath:
                case BookingsAllPath:
                    return true;
                default:
                    return path.startsWith('/bookings/');
            }
        },
        [
            hasGrooming,
            hasMobileGrooming,
            hasDental,
            hasDaycare,
            hasBoarding,
            hasTransportation,
            hasTraining,
            hasEvaluation
        ]
    );

    const defaultBookingPath = React.useMemo(() => {
        const lastBookingPath = localStorage.getItem('last_booking_path');

        // Only use lastBookingPath if it corresponds to an enabled service
        if (lastBookingPath && isBookingPathAllowed(lastBookingPath)) {
            return lastBookingPath;
        }

        if (hasGrooming || hasDental || hasMobileGrooming) {
            return BookingsGroomingPath;
        }

        if (hasDaycare) {
            return readDaycareBookingsView() === 'list' ? BookingsDaycarePath : BookingsKanbanPath;
        }

        if (hasBoarding) {
            return BookingsBoardingPath;
        }

        if (hasEvaluation) {
            return BookingsEvaluationPath;
        }

        if (hasTransportation) {
            return BookingsTransportationPath;
        }

        if (hasTraining) {
            return BookingsTrainingPath;
        }

        if (hasWalking) {
            return BookingsWalkingPath;
        }

        return BookingsGroomingPath;
    }, [
        hasGrooming,
        hasMobileGrooming,
        hasDental,
        hasDaycare,
        hasBoarding,
        hasEvaluation,
        hasTransportation,
        hasTraining,
        hasWalking,
        isBookingPathAllowed
    ]);

    React.useEffect(() => {
        if (location.pathname === BookingsRootPath) {
            return;
        }

        localStorage.setItem('last_booking_path', location.pathname);
    }, [location.pathname]);

    return (
        <>
            <PrivatePage
                pageName="/bookings"
                title="Bookings - Cuddles"
                pageContentClassName={classes.root}
            >
                <BookingsNavbar />
                <div className={classes.body}>
                    <Switch>
                        <Route
                            path={BookingsAbandonedPath}
                            component={AbandonedBookings}
                        />
                        {hasDaycare && (
                            <Route
                                exact
                                path={[BookingsDaycarePath, BookingsDaycareDetailPath, BookingsKanbanPath]}
                                render={props => (
                                    <AppointmentsList
                                        {...props}
                                        listType="daycare"
                                    />
                                )}
                            />
                        )}
                        {hasBoarding && (
                            <Route
                                exact
                                path={[BookingsBoardingPath, BookingsBoardingDetailPath]}
                                render={props => (
                                    <AppointmentsList
                                        {...props}
                                        listType="boarding"
                                    />
                                )}
                            />
                        )}
                        {hasEvaluation && (
                            <Route
                                exact
                                path={[BookingsEvaluationPath, BookingsEvaluationDetailPath]}
                                render={props => (
                                    <AppointmentsList
                                        {...props}
                                        listType="evaluation"
                                    />
                                )}
                            />
                        )}
                        {hasTransportation && (
                            <Route
                                exact
                                path={[BookingsTransportationPath, BookingsTransportationDetailPath]}
                                render={props => (
                                    <AppointmentsList
                                        {...props}
                                        listType="transportation"
                                    />
                                )}
                            />
                        )}
                        {hasTraining && (
                            <Route
                                exact
                                path={[BookingsTrainingPath, BookingsTrainingDetailPath]}
                                render={props => (
                                    <AppointmentsList
                                        {...props}
                                        listType="training"
                                    />
                                )}
                            />
                        )}
                        {hasWalking && (
                            <Route
                                exact
                                path={[BookingsWalkingPath, BookingsWalkingDetailPath]}
                                render={props => (
                                    <AppointmentsList
                                        {...props}
                                        listType="walking"
                                    />
                                )}
                            />
                        )}
                        {hasEvaluation && (
                            <Route
                                exact
                                path={[BookingsEvaluationPath, BookingsEvaluationDetailPath]}
                                render={props => (
                                    <AppointmentsList
                                        {...props}
                                        listType="evaluation"
                                    />
                                )}
                            />
                        )}

                        <Route
                            exact
                            path={[BookingsRequestedPath, BookingsRequestedDetailPath]}
                            render={props => (
                                <AppointmentsList
                                    {...props}
                                    listType="requested"
                                />
                            )}
                        />
                        {(hasDental || hasGrooming || hasMobileGrooming) && (
                            <Route
                                exact
                                path={[BookingsGroomingPath, BookingsGroomingDetailPath]}
                                render={props => (
                                    <AppointmentsList
                                        {...props}
                                        listType="grooming"
                                    />
                                )}
                            />
                        )}
                        <Route
                            exact
                            path={[BookingsAllPath, BookingsAllDetailPath]}
                            render={props => (
                                <AppointmentsList
                                    {...props}
                                    listType="all"
                                />
                            )}
                        />
                        <Redirect
                            path={BookingsPath}
                            to={defaultBookingPath}
                        />
                        <Redirect
                            path={BookingsRootPath}
                            to={defaultBookingPath}
                        />
                    </Switch>
                </div>
            </PrivatePage>
        </>
    );
};

export default withRouter(Bookings);
